// DashBoardb2b.jsx
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import { Helmet } from "react-helmet";
import { Icon } from "@iconify/react";
import moment from "moment";
import { format } from "date-fns";

//components
// import StateDistrictFilter from "./geographyFilter";

// Dashboard Components
import DashboardKPISection from "./DashboardKPI";
import {
  DonutCard,
  DONUT_PANEL_SX,
  useDonutSizing,
  buildTestResultsData,
  buildPatientsData,
  buildGenderData,
} from "./DashboardPiechart";
import DashboardTestsLine from "./DashboardTestLine";
import DashboardCasesBar from "./DashboardCasesBar";
import DashboardTopCentres from "./DashboardTopCentres";
import DashboardCentresSection from "./DashboardCentreBar-Map";
import DashboardFiltersRow, {
  DashboardFilterActions,
} from "./DashboardFilterRow";

// MUI
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  FormControl,
  MenuItem,
  Select,
  ListItemText,
  Checkbox,
  Button,
  Popover,
  Divider,
  useMediaQuery,
  useTheme,
  Fade,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

// MUI X Date pickers (Free)
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// MUI X-Charts
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";

// Recharts
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
  Tooltip,
  Cell,
} from "recharts";

// Google Maps
import {
  useJsApiLoader,
} from "@react-google-maps/api";
import { MAP_LIBRARIES } from "../../utils/mapLibraries";

// Custom
import barsLoading from "../../assets/icons/barsLoading.svg";
import pieChartloading from "../../assets/icons/pieChartloading.svg";
import barChartLoading from "../../assets/icons/barChartLoading.svg";

// APIs & Context
import { AuthContext } from "../../context/AuthContextProvider";
import { useGetAnalyticsSplit, useGetTimeseries } from "../../api/analytics";

// Profile Steps
import ProfileSteps from "../ProfileSteps";

const INTERPRETATION_COLORS = {
  Normal: "#53B253",
  Abnormal: "#FFC46C",
  Critical: "#FB8588",
  Retake: "#C2C9FF",
};

const containerStyle = {
  width: "100%",
  height: "310px",
  borderRadius: "1rem",
};

const containerStyleFullScreen = {
  width: "100%",
  height: "100%",
  borderRadius: "1rem",
};

const LIBRARIES = MAP_LIBRARIES;

const buildLineData = (timeseriesResp, granularity) =>
  ((timeseriesResp || {}).reports || []).map((entry) => {
    const dateStr = entry.day || entry.hour || entry.week || entry.month;
    const date = moment(dateStr);
    let x = "";
    if (granularity === "hour") x = date.format("HH:mm");
    else if (granularity === "day") x = date.format("DD MMM");
    else if (granularity === "week") x = date.format("DD MMM");
    else if (granularity === "month") x = date.format("MMM YY");
    else x = date.format("DD/MM/YYYY");

    const counts = entry.statusCounts || entry.counts || {};
    const normal = Number(counts.normal || 0);
    const abnormal = Number(counts.abnormal || 0);
    const critical = Number(counts.critical || 0);
    const retake = Number(counts.retake || 0);
    const beyond_critical =
      Number(counts.beyond_critical || 0) +
      Number(counts["beyond critical"] || 0);
    const total = normal + abnormal + critical + retake + beyond_critical;
    return { x, normal, abnormal, critical, retake, total, beyond_critical };
  });

const buildBarData = (timeseriesResp, casesGranularity) =>
  ((timeseriesResp || {}).cases || []).map((entry) => {
    const dateStr = entry.date;
    const date = moment(dateStr);
    let x = "";
    if (casesGranularity === "hour") x = date.format("HH:mm");
    else if (casesGranularity === "day") x = date.format("DD MMM");
    else if (casesGranularity === "week") x = date.format("DD MMM");
    else if (casesGranularity === "month") x = date.format("MMM YY");
    else x = date.format("DD/MM/YYYY");
    return { x, count: entry.count || 0 };
  });

const buildCentreData = (analyticsData) => {
  const d = analyticsData || {};
  const groupedByCenter = d.groupedByCenter || [];
  const centerDetails = d.centerDetails || {};
  return groupedByCenter.map((entry) => {
    const [centerId, statusAndCount] = Object.entries(entry)[0] || [];
    const name = centerDetails[centerId]?.name || "Unknown Centre";
    const {
      normal = 0,
      abnormal = 0,
      critical = 0,
      retake = 0,
    } = statusAndCount || {};
    return {
      id: centerId,
      name,
      normal,
      abnormal,
      critical,
      retake,
      total: normal + abnormal + critical + retake,
    };
  });
};

export default function DashBoardb2b() {
  // --- Context ---
  const { all_centres, onReceivePushNotification } = useContext(AuthContext);

  // --- Local UI state ---
  // const [analyticsData, setAnalyticsData] = useState(null);
  // const [timeseriesData, setTimeseriesData] = useState(null);

  const [markers, setMarkers] = useState([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState(null);

  const [user, setUser] = useState({});
  const [selectedCentres, setSelectedCentres] = useState([]);
  const [selectedCentreTitle, setSelectedCentreTitle] = useState("All Centres");

  // Date filter + custom date range (using free DatePickers)
  const [selectedDateFilter, setSelectedDateFilter] = useState("today");
  const [selectedDateTitle, setSelectedDateTitle] = useState("Today");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tempFromDate, setTempFromDate] = useState(null);
  const [tempToDate, setTempToDate] = useState(null);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);

  // geofilter
  const [geoFilter, setGeoFilter] = useState({ state: "", district: "" });

  // Granularity
  const [granularityState, setGranularityState] = useState({
    value: "hour",
    label: "Hourly",
    options: [],
  });
  const [casesGranularityState, setCasesGranularityState] = useState({
    value: "hour",
    label: "Hourly",
    options: [],
  });

  //refersh args to refresh the page
  const [refreshArg, setRefreshArg] = useState(false);

  // debounce + refresh control
  const debounceRef = useRef(null);
  // const [debouncedArgsTicker, setDebouncedArgsTicker] = useState(0);
  const [debouncedArgs, setDebouncedArgs] = useState(() => ({}));
  // const [refreshKey, setRefreshKey] = useState(0);

  // google map
  const mapRef = useRef(null);
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  // mediaquery
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // derived centres list (Hubs + Spokes)
  const allCentres = useMemo(() => {
    if (!Array.isArray(all_centres)) return [];
    const options = all_centres.flatMap((hub) => {
      const hubOption = {
        label: hub?.label ?? hub?.name,
        value: hub?.value ?? hub?.id,
      };
      const spokeOptions =
        hub?.spokeCenters?.map((spoke) => ({
          label: spoke?.name ?? "Unnamed Spoke",
          value: spoke?.id,
        })) || [];
      return [hubOption, ...spokeOptions];
    });
    return options;
  }, [all_centres]);

  // build markers once we have centres
  useEffect(() => {
    if (!Array.isArray(all_centres) || all_centres.length === 0) return;
    const extracted = all_centres.flatMap((hub) => {
      const arr = [];
      const hubLat = parseFloat(hub?.latitude);
      const hubLng = parseFloat(hub?.longitude);
      if (!isNaN(hubLat) && !isNaN(hubLng)) {
        arr.push({
          id: hub?.id || hub?.value,
          name: hub?.name || hub?.label || "Unknown Hub",
          lat: hubLat,
          lng: hubLng,
          address: hub?.fullAddress || "No Address",
          logo: hub?.logo || null,
          type: "Hub",
        });
      }
      if (Array.isArray(hub.spokeCenters)) {
        hub.spokeCenters.forEach((spoke) => {
          const coords = spoke?.address?.location?.coordinates || [];
          const lng = parseFloat(coords[0]);
          const lat = parseFloat(coords[1]);
          if (!isNaN(lat) && !isNaN(lng)) {
            arr.push({
              id: spoke?.id,
              name: spoke?.name || "Unnamed Spoke",
              lat,
              lng,
              address:
                spoke?.address?.fulladdress ||
                `${spoke?.address?.area || ""}, ${spoke?.address?.city || ""}`,
              logo: spoke?.logo || null,
              type: "Spoke",
            });
          }
        });
      }
      return arr;
    });
    setMarkers(extracted);
  }, [all_centres]);

  // user from storage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("@auth_login")) || {};
    setUser(storedUser?.data || {});
  }, []);

  //push notification
  useEffect(() => {
    if (onReceivePushNotification) {
      setRefreshArg((prev) => !prev);
    }
  }, [onReceivePushNotification]);

  // select all centres once on mount (only real centres)
  const setCentresOnceRef = useRef(false);
  useEffect(() => {
    if (setCentresOnceRef.current) return;
    if (allCentres.length > 0) {
      setSelectedCentres(allCentres);
      setSelectedCentreTitle("All Centres");
      setCentresOnceRef.current = true;
    }
  }, [allCentres]);

  // keep title in sync with selection
  useEffect(() => {
    if (
      selectedCentres.length === 0 ||
      selectedCentres.length === allCentres.length
    ) {
      setSelectedCentreTitle("All Centres");
    } else {
      setSelectedCentreTitle(selectedCentres.map((c) => c.label).join(", "));
    }
  }, [selectedCentres, allCentres.length]);

  // granularity rules
  useEffect(() => {
    let value = "hour";
    let label = "Hourly";
    let options = [];

    const isCustom = selectedDateFilter === "custom" && fromDate && toDate;

    if (!isCustom) {
      if (selectedDateFilter === "today") {
        value = "hour";
        label = "Hourly";
      } else if (selectedDateFilter === "last_7_days") {
        value = "day";
        label = "Daily";
      } else if (selectedDateFilter === "last_30_days") {
        value = "week";
        label = "Weekly";
        options = [
          { label: "Weekly", value: "week" },
          { label: "Daily", value: "day" },
        ];
      } else if (selectedDateFilter === "last_3_months") {
        value = "week";
        label = "Weekly";
        options = [
          { label: "Weekly", value: "week" },
          { label: "Monthly", value: "month" },
        ];
      } else if (
        selectedDateFilter === "last_6_months" ||
        selectedDateFilter === "last_12_months"
      ) {
        value = "month";
        label = "Monthly";
      }
    } else {
      const diffDays = Math.max(
        0,
        Math.round(
          (new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24),
        ),
      );
      if (diffDays <= 7) {
        value = "day";
        label = "Daily";
      } else if (diffDays <= 30) {
        value = "week";
        label = "Weekly";
      } else {
        value = "month";
        label = "Monthly";
      }
    }

    setGranularityState({ value, label, options });
    setCasesGranularityState({ value, label, options });
  }, [selectedDateFilter, fromDate, toDate]);

  // apply custom date
  // const applyCustomDates = () => {
  //   if (fromDate && toDate) {
  //     const startDate = new Date(fromDate).toLocaleDateString("en-US", {
  //       day: "numeric",
  //       month: "short",
  //       year: "numeric",
  //     });
  //     const endDate = new Date(toDate).toLocaleDateString("en-US", {
  //       day: "numeric",
  //       month: "short",
  //       year: "numeric",
  //     });
  //     setSelectedDateFilter("custom");
  //     setSelectedDateTitle(`${startDate} - ${endDate}`);
  //     setDateAnchorEl(null);
  //   }
  // };
  const applyCustomDates = () => {
    if (tempFromDate && tempToDate) {
      const startLabel = format(tempFromDate, "dd MMM yyyy");
      const endLabel = format(tempToDate, "dd MMM yyyy");

      setSelectedDateFilter("custom");
      setSelectedDateTitle(`${startLabel} - ${endLabel}`);


      setFromDate(format(tempFromDate, "yyyy-MM-dd"));
      setToDate(format(tempToDate, "yyyy-MM-dd"));

      setDateAnchorEl(null);
    }
  };
  // build args stable
  const builtArgs = useMemo(() => {
    const spandanSelected = selectedCentres.some(
      (c) => c.value === "spandan-doctor",
    );
    const centerIdsSelected = selectedCentres
      .filter((c) => c.value !== "spandan-doctor")
      .map((c) => c.value);

    //  check if all centres selected
    const isAllSelected =
      centerIdsSelected.length === allCentres.length;

    const args = {
      center_selected: isAllSelected ? "all" : "no",
      spandan_doctor: spandanSelected,
      granularity: granularityState.value,
      cases_granularity: casesGranularityState.value,
      filter: selectedDateFilter.toLowerCase(),
      state: geoFilter.state || "",
      district: geoFilter.district || "",
      refreshArg,
    };

    // Send whichever side of the selection is smaller: checked ids (backend
    // applies $in) when fewer centres are checked, unchecked ids (backend
    // applies $nin) when most centres are checked. Keeps the query payload
    // small when there are many centres.
    if (!isAllSelected) {
      const uncheckedIds = allCentres
        .map((c) => c.value)
        .filter((v) => v !== "spandan-doctor" && !centerIdsSelected.includes(v));

      if (centerIdsSelected.length > 0 && uncheckedIds.length < centerIdsSelected.length) {
        args.unchecked_ids = uncheckedIds;
      } else {
        args.checked_ids = centerIdsSelected;
      }
    }

    if (selectedDateFilter === "custom" && fromDate && toDate) {
      args.start = fromDate;
      args.end = toDate;
    }
    return args;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedCentres,
    allCentres,
    granularityState.value,
    casesGranularityState.value,
    selectedDateFilter,
    fromDate,
    toDate,
    geoFilter.state,
    geoFilter.district,
    refreshArg,
  ]);

  // // Debounce updates to builtArgs (so hooks aren't triggered with every tiny state change)
  // useEffect(() => {
  //   if (debounceRef.current) clearTimeout(debounceRef.current);
  //   debounceRef.current = setTimeout(() => {
  //     setDebouncedArgsTicker((t) => t + 1);
  //   }, 350);
  //   return () => clearTimeout(debounceRef.current);
  // }, [
  //   // watch all inputs that influence builtArgs; using builtArgs in deps would cause loop
  //   selectedCentres,
  //   granularityState.value,
  //   casesGranularityState.value,
  //   selectedDateFilter,
  //   fromDate,
  //   toDate,
  //   geoFilter.state,
  //   geoFilter.district,
  //   refreshKey,
  //   allCentres.length,
  // ]);

  // Debounce builtArgs -> set debouncedArgs after 350ms of inactivity
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // store the full args object after debounce
      setDebouncedArgs(builtArgs);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [
    // same deps that affect builtArgs:
    selectedCentres,
    granularityState.value,
    casesGranularityState.value,
    selectedDateFilter,
    fromDate,
    toDate,
    geoFilter.state,
    geoFilter.district,
    refreshArg,
    allCentres.length,
    builtArgs, // optional, safe because builtArgs is memoized
  ]);

  // // final args passed to hooks (memoize for stability)
  // const args = useMemo(() => {
  //   // we simply return the latest builtArgs but include debouncedArgsTicker so the memo updates after debounce
  //   // this causes the hooks to refetch only after debounce fires
  //   return { ...builtArgs, __debounce: debouncedArgsTicker };
  // }, [builtArgs, debouncedArgsTicker]);

  // final args passed to hooks — use the debounced args (no tickers)
  const args = useMemo(() => {
    // ensure we always pass an object (avoid undefined on first render)
    return debouncedArgs && Object.keys(debouncedArgs).length
      ? debouncedArgs
      : builtArgs;
  }, [debouncedArgs, builtArgs]);

  // ---------- USE THE HOOKS directly ----------
  // These hooks are expected to return { analytics, isLoading } and { timeSeries, isLoading }
  // Two requests instead of one: the KPI cards come back on the fast summary
  // call, while the centre map waits on the slower interpretation facet.
  const {
    analytics: analyticsData,
    isLoading: isLoadingAnalytics,
    isLoadingInterpretation,
    error: analyticsError,
  } = useGetAnalyticsSplit(args);

  const {
    timeSeries: timeseriesResp,
    isLoading: isLoadingTimeseries,
    error: timeseriesError,
  } = useGetTimeseries(args);

  const loading = Boolean(isLoadingAnalytics || isLoadingTimeseries);
  // The centre map is fed by the interpretation half, which resolves later than
  // the KPI cards, so it needs its own flag rather than the shared one.
  const centresLoading = Boolean(isLoadingInterpretation);

  // Seeded from cache so a remount with cached SWR data doesn't briefly
  // render empty charts before the effect below runs.
  const [lineData, setLineData] = useState(() =>
    isLoadingTimeseries
      ? []
      : buildLineData(timeseriesResp, granularityState.value),
  );
  const [barData, setBarData] = useState(() =>
    isLoadingTimeseries
      ? []
      : buildBarData(timeseriesResp, casesGranularityState.value),
  );
  const [centreData, setCentreData] = useState(() =>
    isLoadingInterpretation
      ? []
      : buildCentreData(analyticsData),
  );

  // When hooks return data, transform to chart-ready shapes
  useEffect(() => {
    // Each half updates as soon as its own request lands, so a slow one never
    // holds back the parts that are already ready.
    if (!isLoadingTimeseries) {
      setLineData(buildLineData(timeseriesResp, granularityState.value));
      setBarData(buildBarData(timeseriesResp, casesGranularityState.value));
    }
    // The centre map lives on the interpretation half, so it updates on its own
    // clock rather than waiting for every request to finish.
    if (!isLoadingInterpretation) setCentreData(buildCentreData(analyticsData));
  }, [
    isLoadingAnalytics,
    isLoadingInterpretation,
    isLoadingTimeseries,
    analyticsData,
    timeseriesResp,
    granularityState.value,
    casesGranularityState.value,
  ]);

  // manual refresh (increment refreshKey => hooks refetch)
  // const handleRefresh = () => {
  //   setRefreshKey((k) => k + 1);
  // };

  // CSV export (unchanged logic except referencing analyticsData)

  const handleClearFilters = () => {
    setSelectedDateFilter("today");
    setSelectedDateTitle("Today");
    setFromDate(null);
    setToDate(null);

    setGeoFilter({ state: "", district: "" });

    setSelectedCentres(allCentres); // reset to ALL
  };

  const isDefault =
    selectedDateFilter === "today" &&
    !fromDate &&
    !toDate &&
    !geoFilter.state &&
    !geoFilter.district &&
    selectedCentres.length === allCentres.length;

  const handleExportCSV = () => {
    if (!analyticsData) return;

    const summaryData = [
      ["Summary"],
      ["Total Tests Taken", analyticsData.totalTestsTaken ?? "-"],
      ["Total Unique Patients", analyticsData.totalUniquePatients ?? "-"],
      [
        "Avg Interpretation Time (min)",
        Math.floor(analyticsData.avgInterpretationTime ?? 0),
      ],
      ["Cases", analyticsData.cases ?? "-"],
      [],
    ];

    const interpretationData = [
      ["Test Results"],
      ["Status", "Count"],
      ...(analyticsData.interpretationStatusCounts
        ? Object.entries(analyticsData.interpretationStatusCounts).map(
          ([label, value]) => [
            label.charAt(0).toUpperCase() + label.slice(1),
            value,
          ],
        )
        : []),
      [],
    ];

    const patientData = [
      ["Patients"],
      ["Type", "Count"],
      ["New", analyticsData?.patientClassification?.newPatients ?? "-"],
      ["Returning", analyticsData?.patientClassification?.returning ?? "-"],
      [],
    ];

    const lineTable = [
      ["Tests"],
      ["Total", "Normal", "Abnormal", "Critical", "Retake"],
      ...lineData.map((l) => [
        l.total,
        l.normal,
        l.abnormal,
        l.critical,
        l.retake,
      ]),
      [],
    ];

    const barTable = [
      ["Cases"],
      ["Date", "Count"],
      ...barData.map((b) => [b.x, b.count]),
      [],
    ];

    const centreTable = [
      ["Centres"],
      ["Centre", "Normal", "Abnormal", "Critical", "Retake"],
      ...centreData.map((c) => [
        c.name,
        c.normal,
        c.abnormal,
        c.critical,
        c.retake,
      ]),
      [],
    ];

    const fullData = [
      ...summaryData,
      ...interpretationData,
      ...patientData,
      ...lineTable,
      ...barTable,
      ...centreTable,
    ];
    const csvContent = fullData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const today = new Date().toISOString().split("T")[0];
    link.setAttribute("download", `Analytics_Report_${today}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Tooltips for Recharts (unchanged)
  const CustomTooltip2 = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            position: "relative",
            backgroundColor: "var(--app-surface)",
            px: 2,
            py: 1,
            border: "1px solid var(--app-border)",
            borderRadius: "8px",
            fontSize: "13px",

            textAlign: "flex-start",
            lineHeight: 1.2,
          }}
        >
          {/* Arrow */}
          <Box
            sx={{
              position: "absolute",
              left: "-6px",
              top: "50%",
              transform: "translateY(-50%) rotate(45deg)",
              width: "12px",
              height: "12px",
              backgroundColor: "var(--app-surface)",
              borderLeft: "1px solid var(--app-border)",
              borderBottom: "1px solid var(--app-border)",
            }}
          />

          {/* Label */}
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "var(--app-text)" }}>
            {label} :
          </Typography>

          {/* Value */}
          <Typography sx={{ fontSize: "13px", fontWeight: 800, color: "var(--app-text)" }}>
            {payload[0].value}
          </Typography>
        </Box>
      );
    }
    return null;
  };
  const BAR_COLORS = {
    normal: "#53B253",
    abnormal: "#FFC46C",
    critical: "#FB8588",
    retake: "#C2C9FF",
  };

  const CustomTooltip3 = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: "var(--app-surface)",
          px: "16px",
          py: "12px",
          borderRadius: "10px",
          fontSize: "13px",
          color: "var(--app-text)",
          border: "1px solid #F3F4F6",
          minWidth: "160px",
        }}
      >
        <Box
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            mb: "6px",
            textTransform: "uppercase",
          }}
        >
          {label}
        </Box>

        <Box
          sx={{
            borderBottom: "1px solid var(--app-border)",
            my: "6px",
            mb: "8px",
          }}
        />

        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {payload.map((entry, idx) => {
            const color = BAR_COLORS[entry.dataKey]; //  ALWAYS WORKS

            return (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Box
                    component="span"
                    sx={{
                      width: 10,
                      height: 10,
                      backgroundColor: color,
                      borderRadius: "50%",
                      display: "inline-block",
                    }}
                  />
                  <Typography sx={{ fontSize: 13 }}>{entry.name}</Typography>
                </Box>

                <Box component="strong" sx={{ fontSize: 13 }}>
                  {entry.value ?? "-"}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // UI helpers
  const dateMenuOpen = Boolean(dateAnchorEl);
  const handleOpenDateMenu = (e) => setDateAnchorEl(e.currentTarget);
  const handleCloseDateMenu = () => setDateAnchorEl(null);

  const DropdownIcon = (props) => (
    <Icon
      icon="eva:arrow-ios-downward-fill"
      width="20"
      height="20"
      {...props}
    />
  );

  const isAllZero =
    barData && barData.length > 0 && barData.every((item) => item.count === 0);

  const isLineAllZero =
    lineData &&
    lineData.length > 0 &&
    lineData.every((item) => {
      const normal = item.normal ?? 0;
      const abnormal = item.abnormal ?? 0;
      const critical = item.critical ?? 0;
      const retake = item.retake ?? 0;
      const total = item.total ?? normal + abnormal + critical + retake;

      return (
        normal === 0 &&
        abnormal === 0 &&
        critical === 0 &&
        retake === 0 &&
        total === 0
      );
    });

  const stackKeys = ["normal", "abnormal", "critical", "retake"];

  const processedCentreData = centreData.map((item) => {
    let topKey = null;

    for (let i = stackKeys.length - 1; i >= 0; i--) {
      if (item[stackKeys[i]] > 0) {
        topKey = stackKeys[i];
        break;
      }
    }

    return { ...item, __topKey: topKey };
  });

  // Shared "card" surface used to give the dashboard a SaaS look
  const cardSx = {
    backgroundColor: "var(--app-surface)",
    borderRadius: "16px",
    border: "1px solid var(--app-border)",
    overflow: "hidden",
    // Entrance + hover polish (shadow/border only — no transform, so the embedded
    // map stays comfortable to interact with).
    opacity: 0,
    animation: "dashFadeUp .5s ease forwards",
    transition: "border-color .22s ease",
    "&:hover": {
      borderColor: "rgba(87,81,233,0.35)",
    },
  };

  // Donuts are placed individually (rather than as one grouped section) so each
  // can sit beside the time-series chart it belongs with.
  const donutSizing = useDonutSizing();
  const donutCommon = { pieChartloading, ...donutSizing };
  const testResultsData = buildTestResultsData(
    analyticsData,
    INTERPRETATION_COLORS,
  );
  const patientsData = buildPatientsData(analyticsData);
  const genderData = buildGenderData(analyticsData);

  // Two mirrored rows: a wide time-series chart on the left, narrow cards right.
  const chartRowSx = {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      md: "repeat(2, 1fr)",
      lg: "2fr 1fr 1fr",
    },
    gap: { xs: 2, md: 2.5 },
  };

  // At tablet width the row is two columns, so the wide chart takes the whole
  // first line and the two narrow cards sit side by side beneath it.
  const wideChartSx = {
    ...cardSx,
    p: { xs: 1.5, md: 2.5 },
    gridColumn: { md: "span 2", lg: "auto" },
  };

  // JSX (mostly unchanged, just references the local `loading` variable)
  return (
    <>
      <Helmet>
        <title>Spandan - Dashboard</title>
      </Helmet>

      <Box className="main-wrappers">
        <Box
          className="page-wrapper"
          sx={{
            maxHeight: "99vh",
            overflowY: "auto",
            pb: "48px",
            pt: "65px",
            fontFamily: "Figtree",
            backgroundColor: "var(--app-bg)",
          }}
        >
          <Box className="content">
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: { xs: 2, md: 2.5 },
                pt: 0,
                pb: { xs: 4, md: 6 },
              }}
            >
              {/* Page header — hero banner */}
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "16px",
                  background:
                    "linear-gradient(120deg, #4B45D6 0%, #5751E9 45%, #7A74F0 100%)",
                  color: "#fff",
                  px: { xs: 2.5, md: 3 },
                  py: { xs: 2, md: 2.25 },
                  // decorative glow circles
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -70,
                    right: -20,
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.10)",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -60,
                    right: 110,
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.07)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.75)",
                        mb: 0.25,
                        fontFamily: "Figtree",
                      }}
                    >
                      {(() => {
                        const h = new Date().getHours();
                        return h < 12
                          ? "Good morning"
                          : h < 17
                            ? "Good afternoon"
                            : "Good evening";
                      })()}
                    </Typography>

                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 800,
                        fontFamily: "Figtree",
                        fontSize: { xs: "20px", md: "23px" },
                        color: "#fff",
                        lineHeight: 1.2,
                      }}
                    >
                      Welcome, {user?.firstName} {user?.lastName}
                    </Typography>
                  </Box>

                  {/* Right: plain date (not a button) */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      icon="solar:calendar-linear"
                      width="16"
                      height="16"
                      color="rgba(255,255,255,0.85)"
                    />
                    <Typography
                      sx={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: "rgba(255,255,255,0.85)",
                        fontFamily: "Figtree",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {format(new Date(), "EEE, dd MMM yyyy")}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Profile Steps (renders nothing once onboarding is complete) */}
              <ProfileSteps />

              {/* Filters Row — fade only (no transform): a transform would make
                  this card a containing block and clip the inline (disablePortal)
                  centre dropdown. */}
              <Box
                sx={{
                  ...cardSx,
                  animation: "dashFadeIn .5s ease forwards",
                  overflow: "visible",
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 1, md: 1.25 },
                }}
              >
                <DashboardFiltersRow
                  selectedDateTitle={selectedDateTitle}
                  selectedDateFilter={selectedDateFilter}
                  setSelectedDateFilter={setSelectedDateFilter}
                  setSelectedDateTitle={setSelectedDateTitle}
                  fromDate={tempFromDate}
                  toDate={tempToDate}
                  setFromDate={setTempFromDate}
                  setToDate={setTempToDate}
                  dateMenuOpen={dateMenuOpen}
                  dateAnchorEl={dateAnchorEl}
                  handleOpenDateMenu={handleOpenDateMenu}
                  handleCloseDateMenu={handleCloseDateMenu}
                  applyCustomDates={applyCustomDates}
                  geoFilter={geoFilter}
                  setGeoFilter={setGeoFilter}
                  allCentres={allCentres}
                  selectedCentres={selectedCentres}
                  setSelectedCentres={setSelectedCentres}
                  isMdUp={isMdUp}
                  DropdownIcon={DropdownIcon}
                  handleClearFilters={handleClearFilters}
                  isDefault={isDefault}
                  handleExportCSV={handleExportCSV}
                />
              </Box>

              {/* Filter actions — mobile only; desktop keeps them in the card */}
              <DashboardFilterActions
                handleClearFilters={handleClearFilters}
                isDefault={isDefault}
                handleExportCSV={handleExportCSV}
                sx={{
                  display: { xs: "flex", md: "none" },
                  justifyContent: "flex-start",
                  mt: -0.5,
                  px: 0.5,
                }}
              />

              {/*KPI Section*/}
              <Box>
                <DashboardKPISection analyticsData={analyticsData} />
              </Box>

              {/* Row 1 — Tests over time, with Test Results and the centre
                  leaderboard alongside it */}
              <Box sx={chartRowSx}>
                {/*Tests Line Section*/}
                <Box sx={wideChartSx}>
                  <DashboardTestsLine
                    selectedDateFilter={selectedDateFilter}
                    granularityState={granularityState}
                    setGranularityState={setGranularityState}
                    loading={loading}
                    lineData={lineData}
                    isLineAllZero={isLineAllZero}
                    barChartLoading={barChartLoading}
                    chartHeight={280}
                  />
                </Box>

                {/*Test Results Donut*/}
                <DonutCard
                  title="Test Results"
                  accent="#5751E9"
                  cardSx={DONUT_PANEL_SX}
                  data={testResultsData}
                  emptyText="No Data Available"
                  {...donutCommon}
                />

                {/*Top Performing Centres*/}
                <DashboardTopCentres
                  centreData={centreData}
                  isLoading={loading}
                  barsLoading={barsLoading}
                  cardSx={cardSx}
                />
              </Box>

              {/* Row 2 — Cases over time, with the two population donuts */}
              <Box sx={chartRowSx}>
                {/*Cases Bar Section*/}
                <Box sx={wideChartSx}>
                  <DashboardCasesBar
                    selectedDateFilter={selectedDateFilter}
                    casesGranularityState={casesGranularityState}
                    setCasesGranularityState={setCasesGranularityState}
                    loading={loading}
                    barData={barData}
                    isAllZero={isAllZero}
                    CustomTooltip2={CustomTooltip2}
                    barsLoading={barsLoading}
                  />
                </Box>

                {/*Patients Donut*/}
                <DonutCard
                  title="Patients"
                  accent="#5751E9"
                  cardSx={DONUT_PANEL_SX}
                  data={patientsData}
                  emptyText="No Patients"
                  {...donutCommon}
                />

                {/*Tests by Gender Donut*/}
                <DonutCard
                  title="Tests by Gender"
                  accent="#5751E9"
                  cardSx={DONUT_PANEL_SX}
                  data={genderData}
                  emptyText="No Gender Data"
                  {...donutCommon}
                />
              </Box>

              {/* Centres + Map */}
              <Box sx={{ ...cardSx, p: { xs: 1.5, md: 2.5 } }}>
                <DashboardCentresSection
                  centreData={centreData}
                  processedCentreData={processedCentreData}
                  loading={centresLoading}
                  markers={markers}
                  selectedMarkerId={selectedMarkerId}
                  setSelectedMarkerId={setSelectedMarkerId}
                  mapRef={mapRef}
                  isLoaded={isLoaded}
                  containerStyle={containerStyle}
                  containerStyleFullScreen={containerStyleFullScreen}
                  CustomTooltip3={CustomTooltip3}
                  barsLoading={barsLoading}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
